import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { FlatRatePlan } from "@common/schemas/billing.schema";

export type PricingByPlan = Record<FLAT_RATE_PLANS, number>;

type PlanWithMonthlyAmount = Pick<FlatRatePlan, "plan" | "monthlyAmountCents">;

interface ApplyPercentOffParams {
    amountCents: number;
    percentOff: number | null;
}

/**
 * Applies a Stripe `percent_off` discount to a per-unit amount. Rounds to
 * whole cents and only discounts positive amounts, so complimentary/zero
 * lines are left untouched. Single source of truth for the percent-off math
 * shared by the owned-plan pricing preview, the upcoming-invoice builder and
 * the frontend price display.
 */
export function applyPercentOff({
    amountCents,
    percentOff,
}: ApplyPercentOffParams): number {
    if (!percentOff || percentOff <= 0 || amountCents <= 0) {
        return amountCents;
    }
    return Math.round(amountCents * (1 - percentOff / 100));
}

function workspacePricing(
    plans: PlanWithMonthlyAmount[],
    percentOff: number | null = null,
): PricingByPlan {
    return plans.reduce((acc, plan) => {
        acc[plan.plan as FLAT_RATE_PLANS] = applyPercentOff({
            amountCents: plan.monthlyAmountCents,
            percentOff,
        });
        return acc;
    }, {} as PricingByPlan);
}

export function regularWorkspacePricing(
    plans: PlanWithMonthlyAmount[],
): PricingByPlan {
    return workspacePricing(plans);
}

export function organizationOwnedWorkspacePricing(
    plans: PlanWithMonthlyAmount[],
    options: { percentOff?: number | null } = {},
): PricingByPlan {
    return workspacePricing(plans, options.percentOff ?? null);
}

export function clientOwnedWorkspacePricing(
    plans: PlanWithMonthlyAmount[],
): PricingByPlan {
    return workspacePricing(plans);
}
